import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { getServiceRecordDocumentDownloadPath } from "../../../Api/ServiceRecordApi";
import SectionIconTile from "../../../Components/SectionIconTile";
import { TimelineIcon, ServiceDocsIcon } from "../../../Components/Icons/SectionIcons";
import PackSummaryCard from "./PackSummaryCard";
import { DateFormatString, formatDateTime } from "../../../Helper/DatePicker/DateFormatters";
import { getLocalDateFromUTC } from "../../../Helper/DatePicker/DateConverter";
import { getObservationLabel } from "../recordReplacementHelper";

// Documents shown in the bottom block, in the same order as the upload tiles.
const DOC_ORDER = [
    { code: "ICAN_MYCAN", label: "ICAN/MYCAN data (outgoing)" },
    { code: "TESTING_ICAN_MYCAN", label: "Testing ICAN/MYCAN data" },
    { code: "PACK_INSPECTION", label: "Pack inspection document" },
    { code: "FSR", label: "FSR" },
    { code: "OTHER", label: "Other document" },
];

const labelFor = (list = [], code) => {
    const found = (list || []).find((o) => o.code === code);
    return found ? found.label : code || "-";
};

const Chip = ({ children }) => (
    <Box
        sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: "#494949",
            backgroundColor: "#F0F0F0",
            borderRadius: "6px",
            padding: "4px 10px",
            display: "inline-block",
        }}
    >
        {children}
    </Box>
);

const AccentChip = ({ children }) => (
    <Box
        sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#4A4AF4",
            backgroundColor: "#EEF0FF",
            borderRadius: "6px",
            padding: "4px 12px",
            display: "inline-block",
        }}
    >
        {children}
    </Box>
);

const SummaryRow = ({ label, children }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", py: "8px" }}>
        <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>{label}</Typography>
        <Box sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", textAlign: "right", minWidth: 0 }}>
            {children}
        </Box>
    </Box>
);

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);

// Document cell: truncated file name (full name on hover) + a download icon when
// the record is saved (serviceRecordID + documentID present). Renders "-" when no
// document was attached.
const DocValue = ({ doc, serviceRecordID }) => {
    const name = doc?.fileName;
    if (!name) return "-";
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px", minWidth: 0 }}>
            <PrimaryTooltip title={name} placement="top">
                <Box
                    sx={{
                        maxWidth: { xs: "140px", sm: "200px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {name}
                </Box>
            </PrimaryTooltip>
            {serviceRecordID && doc?.documentID && (
                <IconButton
                    size="small"
                    data-testid={`doc-download-${doc.documentID}`}
                    sx={{ color: "#6D72F6", p: "2px" }}
                    onClick={() =>
                        window.open(
                            getServiceRecordDocumentDownloadPath(serviceRecordID, doc.documentID),
                            "_blank"
                        )
                    }
                >
                    <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Box>
    );
};

// Read-only view of a replacement request: one card per pack, then the shared
// documents and timeline. Used three ways — the pre-submit review step, the
// full-page log detail, and the log detail slider — which is why the immutability
// warning, the per-document download and the UTC handling are all props.
export default function RecordReplacementReview({
    formState = {},
    config = {},
    preview = null,
    // Pre-submit review shows the immutability warning; the read-only history
    // detail views reuse this component and pass false to hide it.
    showWarning = true,
    // Set for a SAVED record (detail view) so each document shows a download icon.
    serviceRecordID = null,
    // Detail/history view feeds backend UTC timeline values; convert them to the
    // user's timezone for display. The pre-submit review passes the user-local
    // wall-clock values the picker produced, so it leaves this false.
    timelineIsUtc = false,
}) {
    const [remarksExpanded, setRemarksExpanded] = useState(false);

    const { packs = [], timeline = {}, documents = {} } = formState;

    // Timeline datetimes: convert UTC->user-zone only when the source is backend
    // UTC (detail view). formatDateTime then renders the local wall-clock string.
    const tlDisplay = (value) => {
        if (!value) return "-";
        const local = timelineIsUtc ? getLocalDateFromUTC(value, "YYYY-MM-DD HH:mm:ss") : value;
        return formatDateTime(local, "DD MMM YYYY hh:mm A");
    };

    // "Installed on" is a backend UTC datetime when it came from the pack config, and
    // a date-only value when the technician picked it. Slicing to the calendar day
    // first keeps a hand-picked date from being shifted by the viewer's offset.
    const installedOnDisplay = (value) => {
        if (!value) return "-";
        const hasTime = String(value).trim().length > 10;
        return DateFormatString(
            hasTime ? getLocalDateFromUTC(value, "YYYY-MM-DD") : String(value).slice(0, 10)
        );
    };

    const problemChips = [
        ...(formState.problemsReported || []).map((code) => labelFor(config.problemsReported, code)),
        ...(formState.customProblems || []).map((c) => (c || "").trim()).filter(Boolean),
    ];

    const remarks = formState.remarks || "";
    const remarksLong = remarks.length > 220;
    const remarksShown = remarksExpanded || !remarksLong ? remarks : `${remarks.slice(0, 220)}…`;

    const packCountLabel =
        packs.length === 1 ? "Common across this pack" : `Common across all ${packs.length} packs`;

    const attachedDocs = DOC_ORDER.filter(({ code }) => documents[code]?.fileName);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Immutability warning (pre-submit review only). */}
            {showWarning && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        backgroundColor: "#FDF6E3",
                        border: "1px solid #F5E5B8",
                        borderRadius: "8px",
                        padding: "12px 16px",
                    }}
                >
                    <ErrorIcon sx={{ color: "#B7860B", fontSize: 20 }} />
                    <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#8A6D0B" }}>
                        Review carefully before confirming, as this log can't be deleted afterward.
                    </Typography>
                </Box>
            )}

            {/* Problems reported + the request-level observation. */}
            <Box
                sx={{
                    border: "1px solid #EAEAEA",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    padding: "18px 20px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}>
                        Problems reported
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "flex-end" }}>
                        {problemChips.length ? (
                            problemChips.map((p, i) => <Chip key={i}>{p}</Chip>)
                        ) : (
                            <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>-</Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* One card per pack. */}
            {packs.map((pack) => (
                <PackSummaryCard
                    key={pack.key}
                    pack={pack}
                    fields={{
                        outgoingSerial: dash(pack?.outgoing?.serial),
                        installedOn: installedOnDisplay(pack?.outgoing?.installedOn),
                        warranty: labelFor(config.warrantyOptions, pack?.outgoing?.warrantyStatus),
                        incomingSerial: dash(pack?.incoming?.serial),
                        manufacturingDate: pack?.incoming?.manufacturingDate
                            ? DateFormatString(pack.incoming.manufacturingDate)
                            : "-",
                        grade: labelFor(config.gradeTypes, pack?.incoming?.grade),
                        condition: labelFor(config.conditionOptions, pack?.incoming?.condition),
                        // Rendered per pack: observations belong to the pack's own
                        // warranty answer, not to the request.
                        observations: (pack?.outgoing?.observations || []).length
                            ? pack.outgoing.observations.map((code) => (
                                  <Chip key={code}>{getObservationLabel(config, code)}</Chip>
                              ))
                            : null,
                    }}
                />
            ))}

            {/* Shared documents + timeline. */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    alignItems: "stretch",
                    gap: "16px",
                }}
            >
                <Box
                    sx={{
                        border: "1px solid #EAEAEA",
                        borderRadius: "12px",
                        backgroundColor: "#fff",
                        padding: "18px 20px",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "10px", flexWrap: "wrap" }}>
                        <SectionIconTile icon={<ServiceDocsIcon />} />
                        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A" }}>
                            Documents
                        </Typography>
                        <AccentChip>{packCountLabel}</AccentChip>
                    </Box>
                    {attachedDocs.length ? (
                        attachedDocs.map(({ code, label }) => (
                            <SummaryRow key={code} label={label}>
                                <DocValue doc={documents[code]} serviceRecordID={serviceRecordID} />
                            </SummaryRow>
                        ))
                    ) : (
                        <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>
                            No documents attached
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        border: "1px solid #EAEAEA",
                        borderRadius: "12px",
                        backgroundColor: "#fff",
                        padding: "18px 20px",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "10px" }}>
                        <SectionIconTile icon={<TimelineIcon />} />
                        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A" }}>
                            Timeline
                        </Typography>
                    </Box>
                    <SummaryRow label="Issue reported">{tlDisplay(timeline.busHeldUpAt)}</SummaryRow>
                    <SummaryRow label="Work Start">{tlDisplay(timeline.workStartAt)}</SummaryRow>
                    <SummaryRow label="Work end">{tlDisplay(timeline.workEndAt)}</SummaryRow>
                </Box>
            </Box>

            {/* Remarks */}
            <Box
                sx={{
                    border: "1px solid #EAEAEA",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    padding: "16px 20px",
                }}
            >
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1A1A1A", mb: "4px" }}>
                    Remarks:
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#494949", lineHeight: 1.5 }}>
                    {remarksShown || "-"}
                </Typography>
                {remarksLong && (
                    <Typography
                        onClick={() => setRemarksExpanded((v) => !v)}
                        data-testid="review-remarks-toggle"
                        sx={{ fontSize: "13px", color: "#6D72F6", cursor: "pointer", mt: "4px" }}
                    >
                        {remarksExpanded ? "Read Less" : "Read More"}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
